{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "urn:shotstead:schema:stage-state:v1",
  "title": "Shotstead Stage State v1",
  "type": "object",
  "additionalProperties": false,
  "required": [
    "schema_version",
    "stage_id",
    "stage_version",
    "parent_stage_version",
    "stage_status",
    "coordinate_system",
    "environment",
    "performers",
    "props",
    "camera",
    "motion_cues",
    "continuity_constraints"
  ],
  "properties": {
    "schema_version": { "const": "shotstead.stage-state.v1" },
    "stage_id": { "type": "string", "minLength": 1 },
    "stage_version": { "type": "integer", "minimum": 1 },
    "parent_stage_version": {
      "anyOf": [
        { "type": "integer", "minimum": 1 },
        { "type": "null" }
      ]
    },
    "stage_status": {
      "enum": ["draft", "ready_for_rehearsal"]
    },
    "coordinate_system": {
      "type": "object",
      "additionalProperties": false,
      "required": ["units", "handedness", "up_axis", "forward_convention", "rotation_unit"],
      "properties": {
        "units": { "const": "meters" },
        "handedness": { "const": "right" },
        "up_axis": { "const": "Y" },
        "forward_convention": { "const": "local_negative_Z" },
        "rotation_unit": { "const": "radians" }
      }
    },
    "environment": { "$ref": "#/$defs/environment" },
    "performers": {
      "type": "array",
      "items": { "$ref": "#/$defs/performer" }
    },
    "props": {
      "type": "array",
      "items": { "$ref": "#/$defs/prop" }
    },
    "camera": { "$ref": "#/$defs/camera" },
    "motion_cues": {
      "type": "array",
      "items": { "$ref": "#/$defs/motionCue" }
    },
    "continuity_constraints": {
      "type": "array",
      "items": { "$ref": "#/$defs/continuityConstraint" }
    }
  },
  "$defs": {
    "vector3": {
      "type": "object",
      "additionalProperties": false,
      "required": ["x", "y", "z"],
      "properties": {
        "x": { "type": "number" },
        "y": { "type": "number" },
        "z": { "type": "number" }
      }
    },
    "positiveScale": {
      "type": "object",
      "additionalProperties": false,
      "required": ["x", "y", "z"],
      "properties": {
        "x": { "type": "number", "exclusiveMinimum": 0 },
        "y": { "type": "number", "exclusiveMinimum": 0 },
        "z": { "type": "number", "exclusiveMinimum": 0 }
      }
    },
    "transform": {
      "type": "object",
      "additionalProperties": false,
      "required": ["position", "rotation", "scale"],
      "properties": {
        "position": { "$ref": "#/$defs/vector3" },
        "rotation": { "$ref": "#/$defs/vector3" },
        "scale": { "$ref": "#/$defs/positiveScale" }
      }
    },
    "environment": {
      "type": "object",
      "additionalProperties": false,
      "required": ["environment_ref", "transform"],
      "properties": {
        "environment_ref": { "type": "string", "minLength": 1 },
        "transform": { "$ref": "#/$defs/transform" }
      }
    },
    "performer": {
      "type": "object",
      "additionalProperties": false,
      "required": ["performer_id", "asset_ref", "transform", "facing_target_ref", "motion_ref"],
      "properties": {
        "performer_id": { "type": "string", "minLength": 1 },
        "asset_ref": {
          "anyOf": [{ "type": "string", "minLength": 1 }, { "type": "null" }]
        },
        "transform": { "$ref": "#/$defs/transform" },
        "facing_target_ref": {
          "anyOf": [{ "type": "string", "minLength": 1 }, { "type": "null" }]
        },
        "motion_ref": {
          "anyOf": [{ "type": "string", "minLength": 1 }, { "type": "null" }]
        }
      }
    },
    "prop": {
      "type": "object",
      "additionalProperties": false,
      "required": ["prop_id", "asset_ref", "transform", "state"],
      "properties": {
        "prop_id": { "type": "string", "minLength": 1 },
        "asset_ref": {
          "anyOf": [{ "type": "string", "minLength": 1 }, { "type": "null" }]
        },
        "transform": { "$ref": "#/$defs/transform" },
        "state": { "type": "string" }
      }
    },
    "cameraMovement": {
      "type": "object",
      "additionalProperties": false,
      "required": ["kind", "start_seconds", "duration_seconds", "distance_meters"],
      "properties": {
        "kind": { "enum": ["fixed", "push_in", "lateral"] },
        "start_seconds": { "type": "number", "minimum": 0 },
        "duration_seconds": { "type": "number", "minimum": 0 },
        "distance_meters": { "type": "number", "minimum": 0 }
      }
    },
    "camera": {
      "type": "object",
      "additionalProperties": false,
      "required": ["camera_id", "transform", "target_ref", "fov_degrees", "movement"],
      "properties": {
        "camera_id": { "type": "string", "minLength": 1 },
        "transform": { "$ref": "#/$defs/transform" },
        "target_ref": {
          "anyOf": [{ "type": "string", "minLength": 1 }, { "type": "null" }]
        },
        "fov_degrees": { "type": "number", "minimum": 10, "maximum": 120 },
        "movement": {
          "anyOf": [{ "$ref": "#/$defs/cameraMovement" }, { "type": "null" }]
        }
      }
    },
    "motionPathPoint": {
      "type": "object",
      "additionalProperties": false,
      "required": ["t_seconds", "position"],
      "properties": {
        "t_seconds": { "type": "number", "minimum": 0 },
        "position": { "$ref": "#/$defs/vector3" }
      }
    },
    "motionCue": {
      "type": "object",
      "additionalProperties": false,
      "required": ["cue_id", "performer_id", "action", "start_seconds", "duration_seconds", "path", "target_ref"],
      "properties": {
        "cue_id": { "type": "string", "minLength": 1 },
        "performer_id": { "type": "string", "minLength": 1 },
        "action": { "enum": ["idle", "walk", "stop", "turn", "look"] },
        "start_seconds": { "type": "number", "minimum": 0 },
        "duration_seconds": { "type": "number", "minimum": 0 },
        "path": {
          "type": "array",
          "items": { "$ref": "#/$defs/motionPathPoint" }
        },
        "target_ref": {
          "anyOf": [{ "type": "string", "minLength": 1 }, { "type": "null" }]
        }
      }
    },
    "continuityConstraint": {
      "type": "object",
      "additionalProperties": false,
      "required": ["constraint_id", "subject_ref", "kind", "enabled", "note"],
      "properties": {
        "constraint_id": { "type": "string", "minLength": 1 },
        "subject_ref": { "type": "string", "minLength": 1 },
        "kind": {
          "enum": ["preserve_position", "preserve_rotation", "preserve_state", "preserve_motion", "custom"]
        },
        "enabled": { "type": "boolean" },
        "note": { "type": "string" }
      }
    }
  }
}
